import axios from 'axios';
import { API_BASE_URL } from './config';

const equipmentAPI = axios.create({
  baseURL: `${API_BASE_URL}/equipment`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
equipmentAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Equipment {
  _id?: string;
  id?: string;
  name: string;
  type: string;
  model: string;
  serialNumber: string;
  status: 'available' | 'in-use' | 'maintenance' | 'out-of-service';
  location: string;
  assignedTo?: string;
  project?: string;
  lastMaintenance?: Date;
  nextMaintenance?: Date;
  purchaseDate?: Date;
  value: number;
  specifications?: Record<string, any>;
  maintenanceHistory?: any[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface EquipmentFilters {
  status?: string;
  type?: string;
  location?: string;
  assignedTo?: string;
  project?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const equipmentService = {
  // Get all equipment with filters
  getAll: async (filters?: EquipmentFilters) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    const response = await equipmentAPI.get(`/?${params.toString()}`);
    // Backend returns an object: { equipment, totalPages, currentPage, total }
    // Return just the equipment array for simpler consumption in pages
    return response.data?.equipment ?? [];
  },

  // Get equipment by ID
  getById: async (id: string) => {
    const response = await equipmentAPI.get(`/${id}`);
    return response.data;
  },

  // Create new equipment
  create: async (equipment: Omit<Equipment, '_id' | 'id' | 'createdAt' | 'updatedAt'>) => {
    const response = await equipmentAPI.post('/', equipment);
    return response.data;
  },

  // Update equipment
  update: async (id: string, equipment: Partial<Equipment>) => {
    const response = await equipmentAPI.put(`/${id}`, equipment);
    return response.data;
  },

  // Delete equipment
  delete: async (id: string) => {
    const response = await equipmentAPI.delete(`/${id}`);
    return response.data;
  },

  // Get equipment statistics
  getStatistics: async () => {
    const response = await equipmentAPI.get('/statistics');
    return response.data;
  },

  // Get equipment by project
  getByProject: async (projectId: string) => {
    const response = await equipmentAPI.get(`/project/${projectId}`);
    return response.data;
  },

  // Update equipment status
  updateStatus: async (id: string, status: Equipment['status']) => {
    const response = await equipmentAPI.patch(`/${id}/status`, { status });
    return response.data;
  },

  // Assign equipment to user/project
  assign: async (id: string, assignedTo?: string, project?: string) => {
    const response = await equipmentAPI.patch(`/${id}/assign`, { assignedTo, project });
    return response.data;
  },

  // Add maintenance record
  addMaintenance: async (id: string, maintenanceData: any) => {
    const response = await equipmentAPI.post(`/${id}/maintenance`, maintenanceData);
    return response.data;
  },

  // Get maintenance history
  getMaintenanceHistory: async (id: string) => {
    const response = await equipmentAPI.get(`/${id}/maintenance`);
    return response.data;
  },

  // Bulk update equipment
  bulkUpdate: async (updates: Array<{ id: string; data: Partial<Equipment> }>) => {
    const response = await equipmentAPI.patch('/bulk-update', { updates });
    return response.data;
  },
};

export default equipmentService;